/**
 * LA BARRA DEL NAVEGADOR, DEL COLOR DE LA PÁGINA (21-oct-2026).
 *
 * Chrome en Android (y Safari hasta la 18) pintan su barra con `<meta name="theme-color">`, que estaba fijo en negro
 * (`public/index.html`): en el tema claro salía una franja negra encima de una página clara.
 *
 * 🔑 Se copia el fondo del `<body>`, que ya dice el color correcto en cada caso: el tema, Developer y el login (ver «El
 * fondo del documento» en `styles/tokens.css`). Una sola fuente de verdad: si mañana cambia un fondo, la barra lo sigue
 * sin tocar esto.
 *
 * 📌 Safari 26 ya no lee esta etiqueta: toma el fondo del body directamente, así que en iPhone lo resuelve el CSS.
 */

const TRANSPARENT = /^(transparent|rgba\(0, 0, 0, 0\))$/;

/** Se engancha una vez; devuelve con qué soltarlo. */
export const syncThemeColor = (): (() => void) => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return () => undefined;

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  const target = meta;

  const read = () => {
    const color = getComputedStyle(document.body).backgroundColor;
    if (color && !TRANSPARENT.test(color)) target.content = color;
  };

  /** En el siguiente cuadro: así la clase o el tema que acaban de cambiar ya están aplicados al leer el estilo. */
  let frame = 0;
  const apply = () => {
    if (typeof window.requestAnimationFrame !== 'function') return read();
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(read);
  };

  apply();
  const observer = new MutationObserver(apply);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  /** Con el tema en "system" no cambia ningún atributo: cambia el sistema. */
  const system =
    typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  system?.addEventListener?.('change', apply);

  return () => {
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
    observer.disconnect();
    system?.removeEventListener?.('change', apply);
  };
};
